/**
 * Where a set of leads ended up, as the funnel they actually moved through.
 *
 * Every report on this screen is a slice of one story: a lead arrives, gets
 * screened, gets routed, and is either taken or turned away. The old app
 * organised reports around six "report types" crossed with seven preset views,
 * which meant picking a coordinate on two axes before seeing anything, with
 * labels that overlapped ("Campaign Performance" against "Campaign Revenue").
 * The funnel is the one organising idea the domain actually has.
 *
 * A delivery rate says 41% got through. The funnel says 1,853 were duplicates
 * and 7,442 were turned away by filters — the difference between knowing there
 * is a problem and knowing where it is.
 *
 * Stage order is the lead's lifecycle, not the size of the numbers, so the
 * shape of the bar is comparable between campaigns.
 */
import { Progress, Text, Tooltip } from '@mantine/core';
import { formatNumber } from '../theme';
import { CHART_POSITIVE, MONO_SCALE } from '../charts';
import type { ProportionItem } from '../ui';
import { formatShare } from '../ui/format';
import { useVocab } from '../vocab';

type Count = { status: string; count: number };

/**
 * Delivery statuses grouped into lifecycle stages.
 *
 * An unrecognised status falls into `other` rather than being dropped: a
 * funnel whose segments do not sum to the total it is drawn from is worse than
 * one with an unlabelled slice, and silently discarding a status is how a new
 * engine outcome would go unnoticed.
 */
/*
  Colours follow the dashboard: one green for delivered, the rest steps of
  grey, darkest for the outcome most worth a look (rejected or failed). Every
  stage is labelled with its count, so hue is not needed to tell them apart.
*/
const STAGES: { key: string; label: string; statuses: string[]; colour: string }[] = [
  {
    key: 'screened',
    label: 'Screened before routing',
    statuses: ['duplicate', 'suppressed'],
    colour: MONO_SCALE[4],
  },
  {
    key: 'filtered',
    label: 'Filtered out',
    statuses: ['filtered', 'capped', 'group_filtered', 'group_capped'],
    colour: MONO_SCALE[3],
  },
  {
    key: 'rejected',
    label: 'Rejected or failed',
    statuses: ['rejected', 'error', 'timeout', 'failed', 'no_routes'],
    colour: MONO_SCALE[1],
  },
  { key: 'delivered', label: 'Delivered', statuses: ['delivered'], colour: CHART_POSITIVE },
  { key: 'returned', label: 'Returned', statuses: ['returned'], colour: MONO_SCALE[2] },
];

export type FunnelStage = {
  key: string;
  label: string;
  count: number;
  colour: string;
  pct: number;
};

export function toStages(counts?: Count[] | null): { stages: FunnelStage[]; total: number } {
  /*
    Guarded, not assumed. `funnel` is a required field in the published schema,
    but a client must still survive its absence: a response from a server that
    predates the field, or a cached one, or a shape the runtime validator has
    already flagged as drifted and passed through anyway. Rendering nothing is
    correct here; taking the whole reports page down with it is not, and that
    is exactly what an unguarded `.reduce()` did.
  */
  const rows = Array.isArray(counts) ? counts.filter((c) => c && typeof c.count === 'number') : [];
  const total = rows.reduce((n, c) => n + c.count, 0);
  const claimed = new Set(STAGES.flatMap((s) => s.statuses));
  const other = rows.filter((c) => !claimed.has(c.status)).reduce((n, c) => n + c.count, 0);

  const stages = STAGES.map((stage) => {
    const count = rows
      .filter((c) => stage.statuses.includes(c.status))
      .reduce((n, c) => n + c.count, 0);
    return {
      key: stage.key,
      label: stage.label,
      count,
      colour: stage.colour,
      pct: total > 0 ? (count / total) * 100 : 0,
    };
  }).filter((s) => s.count > 0);

  if (other > 0) {
    stages.push({
      key: 'other',
      label: 'Other',
      count: other,
      colour: MONO_SCALE[5],
      pct: total > 0 ? (other / total) * 100 : 0,
    });
  }
  return { stages, total };
}

/** A single bar, for a table cell. Every segment is hoverable. */
export function FunnelBar({ counts, w }: { counts?: Count[] | null; w?: number }) {
  const v = useVocab();
  const { stages, total } = toStages(counts);
  if (total === 0) return <Text size="xs" c="dimmed">{`no ${v.many('lead')}`}</Text>;

  return (
    <Progress.Root size={8} w={w} radius={4} style={{ gap: 2, background: 'transparent' }}>
      {stages.map((s) => (
        <Tooltip
          key={s.key}
          withArrow
          label={`${s.label}: ${formatNumber(s.count)} (${s.pct.toFixed(1)}%)`}
        >
          <Progress.Section value={s.pct} color={s.colour} style={{ borderRadius: 4 }} />
        </Tooltip>
      ))}
    </Progress.Root>
  );
}

/**
 * The funnel's stages as proportion rows, in lifecycle order rather than by
 * size, so the list reads as the path a lead takes.
 */
export function funnelItems(counts?: Count[] | null): { items: ProportionItem[]; total: number } {
  const { stages, total } = toStages(counts);
  return {
    total,
    items: stages.map((s) => ({
      id: s.key,
      label: s.label,
      value: formatNumber(s.count),
      share: formatShare(s.count, total),
      fraction: total > 0 ? s.count / total : 0,
      color: s.colour,
    })),
  };
}
